import Image from 'next/image';
import Link from 'next/link';

function shouldUseUnoptimizedImage(src = '') {
  const value = String(src || '').trim();
  return value.startsWith('/uploads/') || value.includes('/uploads/');
}

function renderAction(action, index) {
  const variant = action.variant || 'primary';
  const className = `app-button app-button--${variant}`;
  const icon = action.icon;
  const iconSrc = action.iconSrc;

  if (action.external) {
    return (
      <a
        key={`${action.href}-${index}`}
        href={action.href}
        className={className}
      >
        {iconSrc ? <Image src={iconSrc} alt="" width={16} height={16} /> : null}
        {icon ? (
          <span className="hub-button-label__icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {action.label}
      </a>
    );
  }

  return (
    <Link
      key={`${action.href}-${index}`}
      href={action.href}
      className={`gap-2 ${className}`}
    >
      {iconSrc ? (
        <Image src={iconSrc} alt="" width={16} height={16} />
      ) : icon ? (
        <span className="hub-button-label__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {action.label}
    </Link>
  );
}

export function HubButtonLabel({
  icon,
  iconSrc,
  iconNode,
  label,
  helper,
  iconSize = 16,
}) {
  return (
    <span className="hub-button-label">
      {iconNode ? (
        <span className="hub-button-label__icon" aria-hidden="true">
          {iconNode}
        </span>
      ) : iconSrc ? (
        <Image
          src={iconSrc}
          alt=""
          aria-hidden="true"
          width={iconSize}
          height={iconSize}
          className="hub-button-label__icon hub-button-label__icon--image"
        />
      ) : icon ? (
        <span className="hub-button-label__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}

      <span className="hub-button-label__content">
        <span className="hub-button-label__title">{label}</span>
        {helper ? (
          <small className="hub-button-label__helper">{helper}</small>
        ) : null}
      </span>
    </span>
  );
}

// ─── Hub Wordmark ─────────────────────────────────────────────────────────────

function getWordmarkSegments(label, highlight) {
  const text = String(label || '').trim() || 'GameHub';
  const target = typeof highlight === 'string' ? highlight.trim() : '';

  if (!target) {
    return [{ text, highlighted: false }];
  }

  const startIndex = text
    .toLocaleLowerCase()
    .indexOf(target.toLocaleLowerCase());

  if (startIndex === -1) {
    return [{ text, highlighted: false }];
  }

  const segments = [];
  const before = text.slice(0, startIndex);
  const matched = text.slice(startIndex, startIndex + target.length);
  const after = text.slice(startIndex + target.length);

  if (before) {
    segments.push({ text: before, highlighted: false });
  }

  segments.push({ text: matched, highlighted: true });

  if (after) {
    segments.push({ text: after, highlighted: false });
  }

  return segments;
}

export function HubWordmark({
  label = 'GameHub',
  highlight = 'Hub',
  className = '',
}) {
  const text = String(label || '').trim() || 'GameHub';
  const segments = getWordmarkSegments(text, highlight);
  const wordmarkClassName = ['hub-wordmark', className]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={wordmarkClassName} aria-label={text}>
      {segments.map((segment, index) => (
        <span
          key={`${segment.text}-${index}`}
          className={[
            'hub-wordmark__segment',
            segment.highlighted
              ? 'hub-wordmark__segment--highlight'
              : 'hub-wordmark__segment--base',
          ].join(' ')}
        >
          {segment.text}
        </span>
      ))}
    </span>
  );
}

// ─── Hub Hero ─────────────────────────────────────────────────────────────────

export function HubHero({
  bgSrc,
  eyebrow = 'GameHub',
  eyebrowTone = 'primary',
  title,
  description,
  descriptionClassName = '',
  actions = [],

  sub = false,
  accent,
  children,
}) {
  const cls = [
    'hub-hero',
    sub ? 'hub-hero--sub' : '',
    accent ? `hub-hero--${accent}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={cls}>
      {bgSrc ? (
        <>
          <Image
            src={bgSrc}
            alt=""
            role="presentation"
            fill
            className="hub-hero__bg"
            sizes="100vw"
            priority
            unoptimized={shouldUseUnoptimizedImage(bgSrc)}
          />
          <div className="hub-hero__overlay" aria-hidden="true" />
        </>
      ) : null}

      {eyebrow ? (
        <p className={`hub-hero__eyebrow hub-hero__eyebrow--${eyebrowTone}`}>
          {eyebrow}
        </p>
      ) : null}
      {title ? <h1 className="hub-hero__title">{title}</h1> : null}
      {description ? (
        <p className={`hub-hero__copy ${descriptionClassName}`}>
          {description}
        </p>
      ) : null}

      {actions.length > 0 ? (
        <div className="hub-hero__actions">{actions.map(renderAction)}</div>
      ) : null}

      {children}
    </section>
  );
}

// ─── Hub Stat Grid ────────────────────────────────────────────────────────────

export function HubStatGrid({
  items = [],
  tone = 'primary',
  variant = 'default',
  className = '',
}) {
  const gridClassName = [
    'hub-stat-grid',
    variant !== 'default' ? `hub-stat-grid--${variant}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={gridClassName}>
      {items.map((item) => {
        const cardTone = item?.tone || tone;

        return (
          <article
            key={item.label}
            className={`hub-stat-card hub-stat-card--${cardTone}`}
          >
            <span className="hub-stat-card__label">{item.label}</span>
            <strong className="hub-stat-card__value">{item.value}</strong>
            {item.note ? (
              <p className="hub-stat-card__note">{item.note}</p>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

// ─── Hub Section ──────────────────────────────────────────────────────────────

export function HubSection({
  eyebrow,
  eyebrowTone = 'info',
  title,
  description,
  descriptionClassName = '',
  actions,
  extraClasses = '',
  children,
}) {
  const cls = ['hub-section', extraClasses].filter(Boolean).join(' ');

  return (
    <section className={cls}>
      <div className="hub-section__header">
        <div>
          {eyebrow ? (
            <p
              className={`hub-section__eyebrow hub-section__eyebrow--${eyebrowTone}`}
            >
              {eyebrow}
            </p>
          ) : null}
          {title ? <h2 className="hub-section__title">{title}</h2> : null}
          {description ? (
            <p className={`hub-section__description ${descriptionClassName}`}>
              {description}
            </p>
          ) : null}
        </div>
        {actions ? <div className="hub-section__actions">{actions}</div> : null}
      </div>

      {children}
    </section>
  );
}

// ─── Hub Pill ─────────────────────────────────────────────────────────────────

export function HubPill({ children, tone = 'default' }) {
  return <span className={`hub-pill hub-pill--${tone}`}>{children}</span>;
}

// ─── Hub Preview Card ─────────────────────────────────────────────────────────

export function HubPreviewCard({
  href,
  image,
  icon,
  iconSrc,
  title,
  description,
  footerLabel = 'Open',
  eyebrow,
  eyebrowTone = 'info',
  pills = [],
  meta = [],
  imageLoading = 'lazy',
  accent,
}) {
  const cls = ['hub-preview-card', accent ? `hub-preview-card--${accent}` : '']
    .filter(Boolean)
    .join(' ');
  const cardPills = [
    ...pills,
    ...meta.map((item) => ({
      label: `${item.label}: ${item.value}`,
      tone: item.tone || 'default',
    })),
  ];

  return (
    <article className={cls}>
      <Link href={href} className="hub-preview-card__link">
        <div className="hub-preview-card__media">
          {image ? (
            <Image
              src={image}
              alt={title}
              fill
              loading={imageLoading}
              fetchPriority={imageLoading === 'eager' ? 'high' : 'auto'}
              sizes="(min-width: 992px) 30vw, (min-width: 768px) 45vw, 100vw"
              className="hub-preview-card__image"
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <div className="hub-preview-card__placeholder">GameHub</div>
          )}
        </div>

        <div className="hub-preview-card__body">
          {iconSrc ? (
            <Image
              src={iconSrc}
              alt=""
              role="presentation"
              width={18}
              height={18}
              className="hub-preview-card__icon hub-preview-card__icon--image"
            />
          ) : icon ? (
            <span className="hub-preview-card__icon" aria-hidden="true">
              {icon}
            </span>
          ) : null}

          {eyebrow ? (
            <p
              className={`hub-preview-card__eyebrow hub-preview-card__eyebrow--${eyebrowTone}`}
            >
              {eyebrow}
            </p>
          ) : null}
          <h3 className="hub-preview-card__title">{title}</h3>
          {description ? (
            <p className="hub-preview-card__copy">{description}</p>
          ) : null}

          {cardPills.length > 0 ? (
            <div className="hub-pill-row">
              {cardPills.map((pill) => (
                <HubPill key={`${title}-${pill.label}`} tone={pill.tone}>
                  {pill.label}
                </HubPill>
              ))}
            </div>
          ) : null}

          <span className="hub-preview-card__cta">{footerLabel}</span>
        </div>
      </Link>
    </article>
  );
}

// ─── Hub Destination Card ─────────────────────────────────────────────────────
// Large image-led card for the Hub Home page.

export function HubDestCard({
  href,
  bgSrc,
  icon,
  iconSrc,
  title,
  description,
  ctaLabel = 'Explore',
  accent = 'primary',
}) {
  const accentVar =
    accent === 'teams'
      ? 'var(--hub-teams-rgb, 94, 224, 168)'
      : accent === 'games'
        ? 'var(--hub-games-rgb)'
        : `var(--bs-${accent}-rgb)`;

  return (
    <Link
      href={href}
      className="hub-dest-card"
      style={{ '--hub-dest-accent-rgb': accentVar }}
    >
      {bgSrc ? (
        <Image
          src={bgSrc}
          alt=""
          role="presentation"
          fill
          className="hub-dest-card__bg"
          sizes="(min-width: 992px) 33vw, (min-width: 576px) 50vw, 100vw"
          style={{ objectFit: 'cover' }}
        />
      ) : null}
      <div className="hub-dest-card__overlay" aria-hidden="true" />
      <div className="hub-dest-card__body">
        {iconSrc ? (
          <Image
            src={iconSrc}
            alt=""
            role="presentation"
            className="hub-dest-card__icon"
            width={24}
            height={24}
          />
        ) : icon ? (
          <span className="hub-dest-card__icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <h3 className="hub-dest-card__title">{title}</h3>
        {description ? (
          <p className="hub-dest-card__desc">{description}</p>
        ) : null}
        <span className="hub-dest-card__cta">{ctaLabel} →</span>
      </div>
    </Link>
  );
}

// ─── Hub Notice ───────────────────────────────────────────────────────────────

export function HubNotice({ title, children }) {
  return (
    <aside className="hub-notice">
      <h2>{title}</h2>
      <p>{children}</p>
    </aside>
  );
}

// ─── Hub Empty State ──────────────────────────────────────────────────────────

export function HubEmptyState({ title, description, actions = [] }) {
  return (
    <div className="hub-empty-state">
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
      {actions.length > 0 ? (
        <div className="hub-hero__actions">{actions.map(renderAction)}</div>
      ) : null}
    </div>
  );
}

// ─── Hub Pager ────────────────────────────────────────────────────────────────

export function HubPager({ page, pages, hrefBuilder, labels = {} }) {
  if (!pages || pages <= 1) return null;

  const previousLabel = labels.previous || 'Previous page';
  const previousCta = labels.previousCta || '← Prev';
  const nextLabel = labels.next || 'Next page';
  const nextCta = labels.nextCta || 'Next →';
  const paginationLabel = labels.pagination || 'Hub pagination';

  return (
    <nav className="hub-pagination" aria-label={paginationLabel}>
      {page > 1 ? (
        <Link href={hrefBuilder(page - 1)} aria-label={previousLabel}>
          {previousCta}
        </Link>
      ) : (
        <span aria-disabled="true">{previousCta}</span>
      )}

      <span aria-current="page">
        {page} / {pages}
      </span>

      {page < pages ? (
        <Link href={hrefBuilder(page + 1)} aria-label={nextLabel}>
          {nextCta}
        </Link>
      ) : (
        <span aria-disabled="true">{nextCta}</span>
      )}
    </nav>
  );
}
